import React, { useCallback, useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { ConciseWaveType, MilestoneType } from './types'
import { Factory } from '@aytm/app/insights/dashboard/helperTypes'
import { MilestoneModel as MilestoneModelType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { MilestoneModel } from '@aytm/app/insights/dashboard/models/BlockModel.models'
import { getBounds, getNormalizedWaves, packIntoRows } from './timeline.logic'
import { extendRange } from '@aytm/app/insights/dashboard/lib/datetime'
import { useMeasurementContext } from '@aytm/app/insights/dashboard/components/contextProviders/uiGeneralContext'
import { generateUID } from '@aytm/app/insights/dashboard/lib/strings'

import { timelineHooksSelector, timelineHooksValidator, type TimelineHooksSelectorParams } from './selectors'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { makeMilestonesTimelineUpdateEventAction } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'
import type { MilestoneListModel } from '@aytm/app/insights/dashboard/models/types.ClientModel'

export const useInteractionEnabled = (disabled?: boolean) => {
  const { isSharedView, isTracker, currentSplit } = usePool<
    typeof InsightsDashboardPool,
    TimelineHooksSelectorParams,
    InsightsDashboardPoolState
  >(InsightsDashboardPool, timelineHooksSelector, timelineHooksValidator)

  return {
    isAllowedToShow: isTracker && !currentSplit && !disabled,
    isInteractionEnabled: isTracker && !currentSplit && !isSharedView && !disabled
  }
}

export const useWaveTimespans = (containerWidth: number, zoomFactor: number) => {
  const { subjects, blockItems, blockMilestones } = usePool<
    typeof InsightsDashboardPool,
    TimelineHooksSelectorParams,
    InsightsDashboardPoolState
  >(InsightsDashboardPool, timelineHooksSelector, timelineHooksValidator)

  const { measurements: widthMeasurements } = useMeasurementContext()

  const currentViewIds = useMemo(
    () =>
      new window.Set(
        ((blockItems?.toJS() || []) as { waveUid?: string; uid: string }[]).map(v => v.waveUid ?? v.uid).filter(Boolean)
      ),
    [blockItems]
  )

  const { conciseWaves, minWavesBound, maxWavesBound } = useMemo(() => {
    const conciseWaves = Array.from(currentViewIds, id =>
      Object.values(subjects).find(subject => subject.waveUid === id)
    )
      .filter(subject => subject && 'wave' === subject.type && subject.from && subject.to)
      .map(
        subject =>
          ({
            from: new Date(subject.from).getTime(),
            waveUid: subject.waveUid,
            uid: subject.uid,
            title: subject.text,
            to: new Date(subject.to).getTime()
          }) as ConciseWaveType
      )

    const { min, max } = getBounds(conciseWaves)
    if (min === Infinity || 0 === max) {
      return { conciseWaves: [] as ConciseWaveType[], minWavesBound: 0, maxWavesBound: 0 }
    }

    let [minWavesBound, maxWavesBound] = extendRange(min, max, 0.1)
    if (0 === containerWidth) {
      return { minWavesBound, maxWavesBound, conciseWaves }
    }
    let currentWaves = conciseWaves
    let isStable = false
    let count = 2
    while (count-- && !isStable) {
      const transformRatio = (maxWavesBound - minWavesBound) / ((containerWidth || 1) * zoomFactor)
      currentWaves = currentWaves.map(wave => {
        // padding-x: 8px & 8px
        const titleSpan = (widthMeasurements.get(`W${wave.title}`) || 16 + 6 * wave.title.length) * transformRatio
        return Object.assign(wave, {
          titleSpan
        })
      })
      const { min: nextMin, max: nextMax } = getBounds(currentWaves)
      ;[minWavesBound, maxWavesBound] = extendRange(nextMin, nextMax, 0.1)
      isStable = min === nextMin && max === nextMax
    }
    return { minWavesBound, maxWavesBound, conciseWaves: currentWaves }
  }, [containerWidth, currentViewIds, subjects, widthMeasurements, zoomFactor])

  const waveRows = useMemo(() => {
    const filteredNormalizedWaves = getNormalizedWaves(conciseWaves, minWavesBound, maxWavesBound)
    return packIntoRows(filteredNormalizedWaves)
  }, [conciseWaves, maxWavesBound, minWavesBound])

  const saveMilestone = useCallback(
    (entry: Partial<MilestoneType>) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const newMilestone = (MilestoneModel as unknown as Factory).of({
        title: entry.title,
        date: entry.date,
        uid: generateUID()
      }) as MilestoneModelType
      poolDispatch(
        makeMilestonesTimelineUpdateEventAction(blockMilestones.push(newMilestone) as unknown as MilestoneListModel)
      )
    },
    [blockMilestones]
  )
  const updateMilestone = useCallback(
    (entry: MilestoneType) => {
      const milestonesIndex = blockMilestones.findIndex(milestone => milestone.uid === entry.id)
      const nextMilestones =
        milestonesIndex < 0
          ? blockMilestones.push({ uid: generateUID(), title: entry.id, date: entry.date } as MilestoneModelType)
          : blockMilestones.setIn([milestonesIndex, 'title'], entry.title).setIn([milestonesIndex, 'date'], entry.date)

      poolDispatch(makeMilestonesTimelineUpdateEventAction(nextMilestones as unknown as MilestoneListModel))
    },
    [blockMilestones]
  )

  const deleteMilestone = useCallback(
    (id: string) => {
      const nextMilestones = blockMilestones.delete(blockMilestones.findIndex(milestone => milestone.uid === id))
      poolDispatch(makeMilestonesTimelineUpdateEventAction(nextMilestones as unknown as MilestoneListModel))
    },
    [blockMilestones]
  )

  const milestones = useMemo(() => {
    const transformRatio = 1 / ((containerWidth || 1) * zoomFactor)
    const milestonesArray = blockMilestones?.toArray() || []
    return milestonesArray
      .filter(m => m.date)
      .map(milestone => {
        const titleSpan =
          (widthMeasurements.get(`M${milestone.title}`) + 16 || 16 + 6 * milestone.title.length) * transformRatio
        return {
          id: milestone.uid,
          title: milestone.title,
          titleSpan,
          date: milestone.date,
          offset: (new Date(milestone.date).getTime() - minWavesBound) / (maxWavesBound - minWavesBound)
        } as MilestoneType
      })
  }, [containerWidth, maxWavesBound, minWavesBound, blockMilestones, widthMeasurements, zoomFactor])

  return {
    minWavesBound,
    maxWavesBound,
    waveRows,
    milestones,
    saveMilestone,
    updateMilestone,
    deleteMilestone
  }
}

export function useZoom({
  initialZoom = 1,
  minZoomFactor = 1,
  maxZoomFactor = 10,
  zoomChangeFactor = 1.25
}: {
  initialZoom?: number
  minZoomFactor?: number
  maxZoomFactor?: number
  zoomChangeFactor?: number
} = {}) {
  const [zoomFactor, setZoomFactor] = useState<number>(initialZoom)

  const canZoomIn = useMemo(() => zoomFactor < maxZoomFactor, [maxZoomFactor, zoomFactor])
  const canZoomOut = useMemo(() => zoomFactor > minZoomFactor, [minZoomFactor, zoomFactor])

  const onZoomIn = useCallback(() => {
    if (canZoomIn) {
      const nextZoomFactor = Math.min(zoomFactor * zoomChangeFactor, maxZoomFactor)
      setZoomFactor(nextZoomFactor)
    }
  }, [canZoomIn, zoomChangeFactor, maxZoomFactor, zoomFactor])

  const onZoomOut = useCallback(() => {
    if (canZoomOut) {
      const nextZoomFactor = Math.max(zoomFactor / zoomChangeFactor, minZoomFactor)
      setZoomFactor(nextZoomFactor)
    }
  }, [canZoomOut, zoomChangeFactor, minZoomFactor, zoomFactor])

  const onSetZoomFactor = useCallback(
    (value: number) => {
      const nextZoomFactor = Math.min(Math.max(value, minZoomFactor), maxZoomFactor)
      setZoomFactor(nextZoomFactor)
    },
    [maxZoomFactor, minZoomFactor]
  )

  return {
    canZoomIn,
    onZoomIn,
    canZoomOut,
    onZoomOut,
    zoomFactor,
    onSetZoomFactor
  }
}

export function useHorizontalScrollUpdates(breadth: number, initialOffset: number = 0) {
  const [scrollOffset, setScrollOffset] = useState(initialOffset)
  const lastBreadth = useRef<number>(breadth)
  const isScrollingRef = useRef(false)

  const [scrollingElement, setScrollingElement] = useState<HTMLDivElement | null>(null)

  const setScrollingNodeRef: React.LegacyRef<HTMLDivElement> = node => {
    if (node && node.clientWidth) {
      setScrollingElement(node)
      // scroll amount
      setScrollOffset(node.scrollLeft)
      if (!scrollingElement && initialOffset !== node.scrollLeft && !isScrollingRef.current) {
        node.scrollTo({ left: initialOffset })
      }
    }
  }

  useLayoutEffect(() => {
    if (lastBreadth.current > breadth) {
      // reset scroll position
      const newScrollOffset = Math.max(0, scrollOffset - (lastBreadth.current - breadth))
      setScrollOffset(newScrollOffset)
      scrollingElement?.scrollTo({ left: newScrollOffset })
    }
    lastBreadth.current = breadth
  }, [scrollingElement, scrollOffset, breadth])

  const abortRef = useRef(new AbortController())

  useEffect(() => {
    if (!scrollingElement) {
      return void 0
    }
    const element = scrollingElement
    element.addEventListener(
      'scroll',
      () => {
        // scroll amount
        isScrollingRef.current = true
        setScrollOffset(element.scrollLeft)
      },
      { signal: abortRef.current.signal }
    )
    return () => {
      isScrollingRef.current = false
      abortRef.current?.abort()
      abortRef.current = new AbortController()
    }
  }, [scrollingElement])

  return { scrollOffset, setScrollingNodeRef }
}
